/**
 * ArchivedNotifications
 *
 * Shows notifications that have been archived by the user. Clicking items
 * deep-links to the relevant dashboard entity using module metadata.
 */
import React from "react";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { getKeyResultById } from "../../../../api/initiative";
import { getMilestoneById } from "../../../../api/milestones";
import { routes } from "../../../../constants/routes";
import { useNavigate } from "react-router-dom";

/** Generate initials for avatar fallbacks. */
const getInitials = (fullName: string): string => {
  if (!fullName) return "";

  const names = fullName.trim().split(" ");
  if (names.length === 1) {
    return names[0].substring(0, 2).toUpperCase();
  }

  const firstName = names[0];
  const lastName = names.at(-1);
  return `${firstName?.charAt(0)}${lastName?.charAt(0)}`?.toUpperCase();
};

/** Notification shape for archived items. */
interface Notification {
  id: number;
  user: string;
  message: string;
  detail: string;
  time: string;
  icon: string;
  referenceTitle?: string;
  isRead?: boolean;
  respectiveId?: number;
  moduleName?: string;
  moduleId?: number;
}

/** Props for the ArchivedNotifications list. */
const ArchivedNotifications: React.FC<{
  data: Notification[];
  onClose: () => void;
}> = ({ data, onClose }) => {
  const navigate = useNavigate();

  const handleNotificationClick = async (item: Notification) => {// NOSONAR
    const moduleName = item?.moduleName?.trim().toLowerCase();
    const moduleId = item?.moduleId;

    switch (moduleName) {
      case "objectives":
        if (moduleId === 1) {
          onClose();
          const queryParams = new URLSearchParams();
          queryParams.set("dashboardTab", "Objectives");
          queryParams.set("objectiveId", String(item.respectiveId));
          navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
        }
        break;

      case "key results":
        if (moduleId === 2) {
          try {
            const response = await getKeyResultById(item.respectiveId);
            if (
              response.data.success &&
              response?.data?.data?.objectives?.length > 0
            ) {
              const objectiveId = response.data.data.objectives[0].objectiveId;
              onClose();
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Objectives");
              queryParams.set("objectiveId", String(objectiveId));
              queryParams.set("keyresultId", String(item.respectiveId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
            }
          } catch (error) {
            console.error(error);
          }
        }
        break;

      case "initiatives":
        if (moduleId === 3) {
          onClose();
          const queryParams = new URLSearchParams();
          queryParams.set("dashboardTab", "Initiatives");
          queryParams.set("initiativeId", String(item.respectiveId));
          navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
        }
        break;

      case "milestone":
        if (moduleId === 4) {
          try {
            const response = await getMilestoneById(item.respectiveId);
            if (
              response.data.success &&
              response.data.data.linkedObjectivesAndKeyResults?.length > 0 &&
              response.data.data.initiative
            ) {
              const initiativeId = response.data.data.initiative.initiativeId;
              onClose();
              onClose();
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Initiatives");
              queryParams.set("initiativeId", String(initiativeId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
            }
          } catch (error) {
            console.error(error);
          }
        }
        break;

      case "employee management":
        if (moduleId === 5) {
          onClose();
          navigate(routes.ROLESDASHBOARD);
        }
        break;
      default:
        break;
    }
  };

  return (
    <List>
      {data.length === 0 ? (
        <Typography
          sx={{
            textAlign: "center",
            color: "#696F8C",
            marginTop: "20px",
            fontFamily: "Manrope, sans-serif",
            fontSize: "13px",
            fontWeight: 400,
          }}
        >
          No notifications yet. You’re all caught up!
        </Typography>
      ) : (
        data.map((item) => (
          <ListItem
            key={item.id}
            alignItems="flex-start"
            sx={{
              borderBottom: "1px solid #eee",
              backgroundColor: item?.isRead ? "#F9F9F9" : "#FFFFFF",
              "&:last-child": {
                borderBottom: "none", // remove border for last item
              },
              borderRadius: "12px",
              cursor: "pointer",
            }}
            onClick={() => handleNotificationClick(item)}
          >
            {/* Avatar */}
            <ListItemAvatar>
              <Avatar
                src={item.icon}
                alt={item.user}
                sx={{
                  backgroundColor: item.icon ? "transparent" : "#403c6f",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {!item.icon && getInitials(item.user)}
              </Avatar>
            </ListItemAvatar>

            {/* Content */}
            <ListItemText
              primary={
                <Typography
                  variant="subtitle1"
                  fontWeight="600 !important"
                  color="#403C6F"
                  fontSize="14px"
                  fontFamily="Manrope, sans-serif"
                >
                  {item.user}
                </Typography>
              }
              secondary={
                <Box>
                  {/* Message */}
                  <Typography
                    variant="body2"
                    color="#696F8C"
                    fontWeight="400 !important"
                    fontSize="12px"
                    fontFamily="Manrope, sans-serif"
                  >
                    {item.detail}{" "}
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {item.referenceTitle}
                    </Box>
                  </Typography>

                  {/* Detail with purple bar */}
                  {/* Detail with purple bar */}
                  {/* Detail with purple bar */}
                  <Box sx={{ display: "flex", alignItems: "stretch", mt: 0.5 }}>
                    <Box
                      sx={{
                        width: "2px",
                        bgcolor: "#6C4ED2",
                        borderRadius: 1,
                        mr: 1,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="#696F8C"
                      fontWeight="400 !important"
                      fontSize="12px"
                      fontFamily="Manrope, sans-serif"
                      width="calc(100% - 20px)"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {item.message}
                    </Typography>
                  </Box>
                  {/* Time */}
                  <Typography
                    variant="caption"
                    color="#696F8C"
                    fontWeight="400 !important"
                    fontSize="12px"
                    fontFamily="Manrope, sans-serif"
                  >
                    {item.time}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))
      )}
    </List>
  );
};

export default ArchivedNotifications;
